import React from 'react';
import { Box, Button, ButtonOwnProps, Checkbox, FormControlLabel, Grid, Popover, Typography, styled, TypographyProps } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import CalendarButton from '@/components/Focus/common/CalendarButton';
import { FocusTheme } from '@/components/Focus/configs/theme';

type Option = { label: string; value: string };

interface Props {
  label: string;
  showLabelInDropdown?: boolean;
  options: Option[];
  selectedItems: string[];
  buttonSx?: ButtonOwnProps['sx'];
  onChange(key: string, value: boolean): void;
  onClear?(): void;
  onToggleAll?(): void;
  renderLabel?(label: string, value: string): React.ReactNode;
  renderTriggerLabel?(options: Option[], selectedItems: string[]): React.ReactNode;
  minWidth?: number;
}

const CalendarDropdownMultiSelect: React.FC<Props> = ({
  label,
  showLabelInDropdown,
  options,
  selectedItems,
  buttonSx = {},
  onChange,
  onClear,
  onToggleAll,
  renderLabel,
  renderTriggerLabel,
  minWidth
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const defaultButtonText = (
    <>
      {options.find((o) => o.value === selectedItems[0])?.label}
      {selectedItems.length > 1 ? <StyledCount component="span">&nbsp;+{selectedItems.length - 1}</StyledCount> : ''}
    </>
  );

  const buttonText = renderTriggerLabel?.(options, selectedItems) ?? (selectedItems.length > 0 ? defaultButtonText : label);

  return (
    <>
      <StyledCalendarButton sx={buttonSx} onClick={handleClick}>
        <StyledButtonText>{buttonText}</StyledButtonText>
        <StyledDownIcon icon="down" />
      </StyledCalendarButton>

      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        minWidth={minWidth}
      >
        {(showLabelInDropdown || onClear) && (
          <Grid container alignItems="center">
            <Grid item xs>
              {showLabelInDropdown && <StyledDropdownLabel>{label}</StyledDropdownLabel>}
            </Grid>
            <Grid item>
              <StyledClearButton variant="text" size="small" onClick={onClear}>
                Clear
              </StyledClearButton>
            </Grid>
          </Grid>
        )}

        {onToggleAll && (
          <Box>
            <StyledFormControlLabel
              control={
                <StyledCheckbox
                  checked={Boolean(selectedItems.length)}
                  indeterminate={selectedItems.length !== 0 && selectedItems.length !== options.length}
                  onChange={onToggleAll}
                />
              }
              label={selectedItems.length ? 'Clear All' : 'Select All'}
              labelPlacement="start"
            />
          </Box>
        )}

        {options.map(({ label, value }) => (
          <Box key={value}>
            <StyledFormControlLabel
              control={<StyledCheckbox checked={selectedItems.includes(value)} onChange={(e) => onChange(value, e.target.checked)} />}
              label={renderLabel ? renderLabel(label, value) : label}
              labelPlacement="start"
            />
          </Box>
        ))}
      </StyledPopover>
    </>
  );
};

export default CalendarDropdownMultiSelect;

const checkboxColor = (theme: any) => (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark);

const StyledCalendarButton = styled(CalendarButton)`
  display: flex;
  padding-right: 8px;
`;

const StyledButtonText = styled(Box)`
  min-width: 0;
  flex-shrink: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledDownIcon = styled(PolarisIcon)`
  margin-left: 8px;
`;

const StyledCount = styled(Typography)<TypographyProps>`
  font-weight: 700;
  color: ${({ theme }) => (theme as FocusTheme).customOverrides.Calendar.textSecondary};
`;

const StyledDropdownLabel = styled(Box)`
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  font-size: 0.857rem;
  text-transform: uppercase;
  color: ${({ theme }) => (theme as FocusTheme).customOverrides.Calendar.textSecondary};
`;

const StyledClearButton = styled(Button)`
  min-width: auto;
  color: ${({ theme }) => checkboxColor(theme)};
`;

const StyledCheckbox = styled(Checkbox)`
  padding: 0;

  &.Mui-checked {
    color: ${({ theme }) => checkboxColor(theme)};
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  display: flex;
  margin-left: 0;
  margin-right: 0;
  padding: 8px;

  & .MuiTypography-root {
    flex-grow: 1;
  }
`;

const StyledPopover = styled(Popover, {
  shouldForwardProp: (prop) => prop !== 'minWidth'
})<{ minWidth?: number }>(({ theme, minWidth }) => ({
  '& .MuiPaper-root': {
    minWidth: minWidth ?? 250,
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(1),
    backgroundColor: `${(theme as FocusTheme).customOverrides.Calendar.background} !important`,
    border: '1px solid #000000'
  }
}));
